import React, { useState, useEffect } from 'react';
import { 
  PlusOutlined,
  UploadOutlined,
  ReloadOutlined,
  SearchOutlined,
  FilterOutlined,
  DeleteOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseOutlined,
  FileExcelOutlined,
  TeamOutlined,
  BankOutlined,
  HeartTwoTone
} from '@ant-design/icons';
import { API_URL } from '../../config';

interface Candidate {
  candidate_id: string;
  person_id: string;
  chart_id: string;
  person_name: string;
  job_title: string;
  status: string;
  hris_type: string;
  hris_ref: string;
  created_at: string;
  date_of_birth: string;
  time_of_birth: string;
  timezone: string;
  gender: string;
  chart_data: any;
}

export const Candidates: React.FC = () => {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [fitFilter, setFitFilter] = useState('');

  // Modals
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);

  // Form states
  const [formName, setFormName] = useState('');
  const [formDob, setFormDob] = useState('1990-05-15');
  const [formTob, setFormTob] = useState('09:00');
  const [formTz, setFormTz] = useState('Asia/Jakarta');
  const [formGender, setFormGender] = useState<'male' | 'female'>('male');
  const [formJobTitle, setFormJobTitle] = useState('Software Engineer');

  // Batch states
  const [batchCsvText, setBatchCsvText] = useState(
    "person_name,date_of_birth,time_of_birth,timezone,gender,job_title\n" +
    "Joko Susilo,1987-12-05,08:30,Asia/Jakarta,male,Product Manager\n" +
    "Rina Mariana,1994-06-20,11:15,Asia/Jakarta,female,UI/UX Designer"
  );

  const ENTERPRISE_HEADERS = {
    'Authorization': 'Bearer dev_api_key',
    'X-Tenant-Id': 'test-tenant',
    'Content-Type': 'application/json'
  };

  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/v1/candidates`, {
        headers: ENTERPRISE_HEADERS
      });
      if (res.ok) {
        const data = await res.json();
        setCandidates(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch candidates', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddCandidate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName || !formDob || !formTob || !formTz || !formJobTitle) return;

    try {
      const res = await fetch(`${API_URL}/v1/candidates`, {
        method: 'POST',
        headers: ENTERPRISE_HEADERS,
        body: JSON.stringify({
          person_name: formName,
          date_of_birth: formDob,
          time_of_birth: formTob,
          timezone: formTz,
          gender: formGender,
          job_title: formJobTitle,
          status: 'applied'
        })
      });
      if (res.ok) {
        setAddModalOpen(false);
        setFormName('');
        fetchCandidates();
      }
    } catch (err) {
      console.error('Failed to create candidate', err);
    }
  };

  const handleBatchUpload = async () => {
    try {
      const lines = batchCsvText.trim().split('\n');
      if (lines.length < 2) return;

      const batchCandidates = lines.slice(1).map(line => {
        const values = line.split(',');
        return {
          person_name: values[0]?.trim(),
          date_of_birth: values[1]?.trim(),
          time_of_birth: values[2]?.trim(),
          timezone: values[3]?.trim(),
          gender: values[4]?.trim() || 'male',
          job_title: values[5]?.trim()
        };
      });

      const res = await fetch(`${API_URL}/v1/candidates/batch`, {
        method: 'POST',
        headers: ENTERPRISE_HEADERS,
        body: JSON.stringify({ candidates: batchCandidates })
      });

      if (res.ok) {
        setBatchModalOpen(false);
        fetchCandidates();
      }
    } catch (err) {
      console.error('Batch screening failed', err);
    }
  };

  const handleHrisSync = async (hrisType: string) => {
    try {
      const res = await fetch(`${API_URL}/v1/candidates/sync-hris`, {
        method: 'POST',
        headers: ENTERPRISE_HEADERS,
        body: JSON.stringify({ hris_type: hrisType })
      });
      if (res.ok) {
        setSyncModalOpen(false);
        fetchCandidates();
      }
    } catch (err) {
      console.error('HRIS Sync failed', err);
    }
  };

  const handleDeleteCandidate = async (id: string) => {
    if (!window.confirm('Delete candidate profile?')) return;
    try {
      const res = await fetch(`${API_URL}/v1/candidates/${id}`, {
        method: 'DELETE',
        headers: ENTERPRISE_HEADERS
      });
      if (res.ok) {
        if (selectedCandidate?.candidate_id === id) {
          setSelectedCandidate(null);
        }
        fetchCandidates();
      }
    } catch (err) {
      console.error('Failed to delete candidate', err);
    }
  };

  // Filter candidates
  const filteredCandidates = candidates.filter(cand => {
    const matchesSearch = cand.person_name.toLowerCase().includes(search.toLowerCase()) || 
                          cand.job_title.toLowerCase().includes(search.toLowerCase());
    const matchesFit = fitFilter ? cand.chart_data?.bazi_compatibility_label === fitFilter : true;
    return matchesSearch && matchesFit;
  });

  const getFitBadgeColor = (label: string) => {
    switch (label) {
      case 'VERY GOOD': return 'bg-[#e8f4fd] text-[#0071C1] border-sky-200';
      case 'GOOD': return 'bg-[#B5E6A2] text-[#2d6b1e] border-emerald-200';
      case 'AVERAGE': return 'bg-[#FFFF65] text-[#555000] border-yellow-200';
      case 'CHALLENGE': return 'bg-[#FF66FF] text-[#990099] border-fuchsia-200';
      case 'BAD': return 'bg-[#ffe6ee] text-[#FF0068] border-rose-200';
      default: return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">
            Candidate Screening Hub
          </h1>
          <p className="text-slate-500 text-sm">
            Analyze applicant character traits, internal self-clashes, and transit alignment.
          </p>
        </div>

        <div className="flex gap-2">
          <button 
            onClick={() => setAddModalOpen(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm rounded shadow transition-colors"
          >
            <PlusOutlined style={{ fontSize: 16 }} />
            Add Candidate
          </button>
          <button 
            onClick={() => setBatchModalOpen(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-slate-50 border border-slate-350 text-slate-700 font-semibold text-sm rounded shadow transition-colors"
          >
            <UploadOutlined style={{ fontSize: 16 }} />
            Import CSV
          </button>
          <button 
            onClick={() => setSyncModalOpen(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm rounded shadow transition-colors"
          >
            <ReloadOutlined style={{ fontSize: 16 }} />
            Sync HRIS
          </button>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col md:flex-row gap-4 items-center shadow-sm">
        <div className="relative flex-1 w-full">
          <SearchOutlined style={{ position: 'absolute', left: 12, top: 10, color: '#94a3b8', fontSize: 18 }} />
          <input 
            type="text" 
            placeholder="Search candidate name or job title..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-250 rounded bg-slate-50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 text-sm"
          />
        </div>

        <div className="flex w-full md:w-auto gap-3">
          <div className="flex items-center gap-2">
            <FilterOutlined style={{ fontSize: 16, color: '#64748b' }} />
            <select
              value={fitFilter}
              onChange={(e) => setFitFilter(e.target.value)}
              className="px-3 py-2 border border-slate-250 rounded bg-white text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="">All Fit Levels</option>
              <option value="VERY GOOD">VERY GOOD (Biru)</option>
              <option value="GOOD">GOOD (Hijau)</option>
              <option value="AVERAGE">AVERAGE (Kuning)</option>
              <option value="CHALLENGE">CHALLENGE (Fuchsia)</option>
              <option value="BAD">BAD (Pink)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Grid: Split List + Detail */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
        {/* Candidates Table */}
        <div className="xl:col-span-2 bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500 font-medium">Loading candidate profiles...</div>
          ) : filteredCandidates.length === 0 ? (
            <div className="p-8 text-center text-slate-400">No candidates match the search filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 font-semibold text-slate-600">
                    <th className="p-4">Name</th>
                    <th className="p-4">Job Vacancy</th>
                    <th className="p-4">Fit Status</th>
                    <th className="p-4">HRIS Status</th>
                    <th className="p-4 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredCandidates.map((cand) => {
                    const label = cand.chart_data?.bazi_compatibility_label || 'N/A';
                    return (
                      <tr key={cand.candidate_id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="p-4">
                          <div className="font-bold text-slate-900">{cand.person_name}</div>
                          <div className="text-xs text-slate-500">{cand.date_of_birth} ({cand.gender === 'female' ? 'Female ♀' : 'Male ♂'})</div>
                        </td>
                        <td className="p-4">
                          <div className="font-semibold text-slate-700">{cand.job_title}</div>
                        </td>
                        <td className="p-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${getFitBadgeColor(label)}`}>
                            {label}
                          </span>
                        </td>
                        <td className="p-4">
                          {cand.hris_type && cand.hris_type !== 'local' ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700 font-bold text-xs bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                              <CheckCircleOutlined style={{ fontSize: 10 }} />
                              Synced ({cand.hris_type})
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-slate-600 font-semibold text-xs bg-slate-100 px-2 py-0.5 rounded border border-slate-250">
                              Local Profile
                            </span>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() => setSelectedCandidate(cand)}
                              className="p-1.5 hover:bg-indigo-50 text-indigo-600 rounded border border-slate-200 hover:border-indigo-200 transition-colors"
                              title="View detailed BaZi analysis"
                            >
                              <EyeOutlined style={{ fontSize: 16 }} />
                            </button>
                            <button
                              onClick={() => handleDeleteCandidate(cand.candidate_id)}
                              className="p-1.5 hover:bg-rose-50 text-rose-600 rounded border border-slate-200 hover:border-rose-200 transition-colors"
                              title="Delete candidate profile"
                            >
                              <DeleteOutlined style={{ fontSize: 16 }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Detailed Workspace Panel */}
        <div className="xl:col-span-1">
          {selectedCandidate ? (
            <div className="bg-white border-2 border-slate-900 rounded-xl shadow-lg p-5 space-y-6 sticky top-20">
              <div className="flex justify-between items-start border-b-2 border-slate-900 pb-3">
                <div>
                  <h2 className="text-lg font-black text-slate-900">{selectedCandidate.person_name}</h2>
                  <p className="text-xs font-bold text-slate-500">{selectedCandidate.job_title} • {selectedCandidate.gender === 'female' ? 'Female' : 'Male'}</p>
                </div>
                <button 
                  onClick={() => setSelectedCandidate(null)}
                  className="p-1 hover:bg-slate-100 rounded border border-slate-200 text-slate-500"
                >
                  <CloseOutlined style={{ fontSize: 16 }} />
                </button>
              </div>

              {/* Element distribution */}
              {selectedCandidate.chart_data?.elements && (
                <div className="space-y-2">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Elements Balance Map</h3>
                  <div className="grid grid-cols-5 gap-1.5 text-center text-xs font-bold text-white">
                    <div className="bg-emerald-600 p-2 rounded">
                      <div>Wood</div>
                      <div className="text-sm font-extrabold">{selectedCandidate.chart_data.elements.wood}</div>
                    </div>
                    <div className="bg-red-600 p-2 rounded">
                      <div>Fire</div>
                      <div className="text-sm font-extrabold">{selectedCandidate.chart_data.elements.fire}</div>
                    </div>
                    <div className="bg-amber-600 p-2 rounded">
                      <div>Earth</div>
                      <div className="text-sm font-extrabold">{selectedCandidate.chart_data.elements.earth}</div>
                    </div>
                    <div className="bg-slate-600 p-2 rounded">
                      <div>Metal</div>
                      <div className="text-sm font-extrabold">{selectedCandidate.chart_data.elements.metal}</div>
                    </div>
                    <div className="bg-blue-600 p-2 rounded">
                      <div>Water</div>
                      <div className="text-sm font-extrabold">{selectedCandidate.chart_data.elements.water}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Self clash Warning card */}
              {selectedCandidate.chart_data?.internal_clashes && selectedCandidate.chart_data.internal_clashes.length > 0 && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-800 space-y-1">
                  <div className="font-extrabold flex items-center gap-1.5">
                    <WarningOutlined style={{ fontSize: 14 }} />
                    Active Self-Clash Detected:
                  </div>
                  {selectedCandidate.chart_data.internal_clashes.map((clash: any, cIdx: number) => (
                    <div key={cIdx} className="font-medium">
                      • <strong className="font-bold">{clash.name}</strong>: {clash.impact}
                    </div>
                  ))}
                </div>
              )}

              {/* Tactical Strategy Guide */}
              {selectedCandidate.chart_data?.strategy_guide && (
                <div className="space-y-2">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Tactical HR Management strategies</h3>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                    <div className="border border-slate-200 rounded p-2.5 bg-slate-50">
                      <strong className="text-xs text-slate-800 block"><TeamOutlined style={{ marginRight: 4, fontSize: 12 }} /> Hubungan dengan Rekan Kerja (Partner)</strong>
                      <p className="text-xs text-slate-600 mt-1">{selectedCandidate.chart_data.strategy_guide.partner}</p>
                    </div>
                    <div className="border border-slate-200 rounded p-2.5 bg-slate-50">
                      <strong className="text-xs text-slate-800 block"><BankOutlined style={{ marginRight: 4, fontSize: 12 }} /> Etos Kerja & Karir (Pekerjaan)</strong>
                      <p className="text-xs text-slate-600 mt-1">{selectedCandidate.chart_data.strategy_guide.pekerjaan}</p>
                    </div>
                    <div className="border border-slate-200 rounded p-2.5 bg-slate-50">
                      <strong className="text-xs text-slate-800 block"><HeartTwoTone twoToneColor="#e03131" style={{ marginRight: 4, fontSize: 12 }} /> Ketahanan Mental & Fisik (Kesehatan)</strong>
                      <p className="text-xs text-slate-600 mt-1">{selectedCandidate.chart_data.strategy_guide.kesehatan}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-400 shadow-sm border-dashed">
              Select a candidate from the list to view their detailed astrological balance and HR strategic advice.
            </div>
          )}
        </div>
      </div>

      {/* Modal: Add Candidate */}
      {addModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white border-2 border-slate-900 rounded-xl shadow-2xl w-full max-w-md p-6 relative">
            <button 
              onClick={() => setAddModalOpen(false)}
              className="absolute right-4 top-4 p-1 hover:bg-slate-100 rounded text-slate-500 border border-slate-200"
            >
              <CloseOutlined style={{ fontSize: 16 }} />
            </button>
            
            <h2 className="text-lg font-black text-slate-900 mb-4 flex items-center gap-1.5">
              Screen Single Candidate
            </h2>

            <form onSubmit={handleAddCandidate} className="space-y-4 text-sm">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Candidate Name</label>
                <input 
                  type="text" 
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g. Dian Permata"
                  className="w-full border border-slate-250 p-2 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Birth Date</label>
                  <input 
                    type="date" 
                    value={formDob}
                    onChange={(e) => setFormDob(e.target.value)}
                    className="w-full border border-slate-250 p-2 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Birth Time</label>
                  <input 
                    type="time" 
                    value={formTob}
                    onChange={(e) => setFormTob(e.target.value)}
                    className="w-full border border-slate-250 p-2 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Timezone</label>
                  <input 
                    type="text" 
                    value={formTz}
                    onChange={(e) => setFormTz(e.target.value)}
                    className="w-full border border-slate-250 p-2 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Gender</label>
                  <select 
                    value={formGender}
                    onChange={(e) => setFormGender(e.target.value as any)}
                    className="w-full border border-slate-250 p-2 rounded bg-white focus:outline-none"
                  >
                    <option value="male">Male ♂</option>
                    <option value="female">Female ♀</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Job vacancy</label>
                <input 
                  type="text" 
                  value={formJobTitle}
                  onChange={(e) => setFormJobTitle(e.target.value)}
                  className="w-full border border-slate-250 p-2 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  required
                />
              </div>

              <button 
                type="submit"
                className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-sm rounded shadow transition-colors"
              >
                Screen Candidate Profile
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Batch Upload */}
      {batchModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white border-2 border-slate-900 rounded-xl shadow-2xl w-full max-w-lg p-6 relative">
            <button 
              onClick={() => setBatchModalOpen(false)}
              className="absolute right-4 top-4 p-1 hover:bg-slate-100 rounded text-slate-500 border border-slate-200"
            >
              <CloseOutlined style={{ fontSize: 16 }} />
            </button>
            
            <h2 className="text-lg font-black text-slate-900 mb-2 flex items-center gap-1.5">
              <FileExcelOutlined style={{ fontSize: 16, color: '#4f46e5' }} />
              Batch Screen Candidates
            </h2>
            <p className="text-slate-500 text-xs mb-4">
              Paste your CSV formatted applicant list below. Columns: <code>person_name, date_of_birth, time_of_birth, timezone, gender, job_title</code>
            </p>

            <div className="space-y-4">
              <textarea 
                value={batchCsvText}
                onChange={(e) => setBatchCsvText(e.target.value)}
                rows={8}
                className="w-full border border-slate-250 p-3 rounded font-mono text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-slate-50 focus:bg-white"
              />

              <button 
                onClick={handleBatchUpload}
                className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-sm rounded shadow transition-colors"
              >
                Import & Screen Candidates
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Sync HRIS */}
      {syncModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white border-2 border-slate-900 rounded-xl shadow-2xl w-full max-w-sm p-6 relative">
            <button 
              onClick={() => setSyncModalOpen(false)}
              className="absolute right-4 top-4 p-1 hover:bg-slate-100 rounded text-slate-500 border border-slate-200"
            >
              <CloseOutlined style={{ fontSize: 16 }} />
            </button>
            
            <h2 className="text-lg font-black text-slate-900 mb-2 flex items-center gap-1.5">
              HRIS Integrations
            </h2>
            <p className="text-slate-500 text-xs mb-4">
              Simulate candidate synchronization from active HR platforms.
            </p>

            <div className="space-y-2">
              <button 
                onClick={() => handleHrisSync('odoo')}
                className="w-full py-3 px-4 border border-slate-250 hover:bg-slate-50 text-slate-700 font-bold text-sm rounded flex items-center justify-between transition-colors"
              >
                <span>Sync from Odoo ERP</span>
                <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded border border-indigo-155">Official</span>
              </button>

              <button 
                onClick={() => handleHrisSync('talenta')}
                className="w-full py-3 px-4 border border-slate-250 hover:bg-slate-50 text-slate-700 font-bold text-sm rounded flex items-center justify-between transition-colors"
              >
                <span>Sync from Mekari Talenta</span>
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded border border-slate-200">Custom API</span>
              </button>

              <button 
                onClick={() => handleHrisSync('bamboohr')}
                className="w-full py-3 px-4 border border-slate-250 hover:bg-slate-50 text-slate-700 font-bold text-sm rounded flex items-center justify-between transition-colors"
              >
                <span>Sync from BambooHR</span>
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded border border-slate-200">Webhook</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default Candidates;
